import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiDocumentText } from "react-icons/hi2";
import { exportToExcel } from "react-json-to-excel";
import { SearchInput, SingleSelect } from "../../../components/common/Search";
import { ENTITY } from "../../../redux/slices/timeline";
import "./CadenceDataTable.css";
import CadenceDataTableColumnHandler from "./CadenceDataTableColumnHandler";
import { DataList } from "../../../components/common/DataList";

export interface Column {
  key: string;
  label: string;
  render?: (row: any, idx: number) => React.ReactNode;
  headerRender?: () => React.ReactNode;
  sortValue?: (row: any) => any;
  sortable?: boolean;
  searchValue?: (row: any) => string | number | null | undefined;
  columnProps?: React.TdHTMLAttributes<HTMLTableCellElement>;
  headerProps?: React.ThHTMLAttributes<HTMLTableHeaderCellElement>;
}

export interface CadenceDataTableProps {
  columns: Column[];
  data: any[];
  extraFilterColumns?: Column[];
  filterDropdown?: React.ReactNode;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  onRowClick?: (row: any) => void;
  columnVisibilityKey?: string;
  defaultVisible?: Record<string, boolean>;
  highlightedRowId?: string | null;
  columnWeights?: Record<string, number>;
  entityType?: ENTITY | string; // Optional, for styling/logic if needed
  
  // Column Management
  // These are now handled internally, but keeping optional props if parent wants to observe or override (optional)
  onColumnOrderChange?: (newOrder: string[]) => void;
  onColumnVisibilityChange?: (columnKey: string) => void;
  onResetColumns?: () => void;
  hiddenColumnKeys?: string[];
  fixedColumnKeys?: string[];
}

const CadenceDataTable: React.FC<CadenceDataTableProps> = ({
  columns,
  data,
  extraFilterColumns = [],
  filterDropdown,
  loading = false,
  emptyMessage = null,
  onRowClick,
  columnVisibilityKey = "",
  defaultVisible = {},
  highlightedRowId = null,
  columnWeights = {},
  entityType = "",
  // onColumnOrderChange, // derived internally
  // onColumnVisibilityChange, // derived internally
  // onResetColumns, // derived internally
  hiddenColumnKeys = [],
  fixedColumnKeys = [],
}) => {
  const [sortedColumn, setSortedColumn] = useState<{
    key: string;
    asc: boolean;
  } | null>(null);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);

  // --- Search State ---
  const [searchField, setSearchField] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTerms, setSearchTerms] = useState<string[]>([]);

  // Update searchTerm and handle comma creation of chips
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // If the input ends with a comma, try to add the term
    if (val.endsWith(',')) {
      const newTerm = val.slice(0, -1).trim();
      if (newTerm) {
        if (!searchTerms.includes(newTerm)) {
          setSearchTerms(prev => [...prev, newTerm]);
        }
        setSearchTerm("");
      } else {
        // Just clear if it was only a comma
        setSearchTerm("");
      }
    } else {
      setSearchTerm(val);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = searchTerm.trim();
      if (val) {
        if (!searchTerms.includes(val)) {
            setSearchTerms(prev => [...prev, val]);
        }
        setSearchTerm("");
      }
    }
  };


  const handleRemoveTerm = (termToRemove: string) => {
    setSearchTerms(prev => prev.filter(t => t !== termToRemove));
  };

  // --- Column Order State ---
  // Read saved column order from localStorage
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (columnVisibilityKey) {
      try {
        const saved = localStorage.getItem(`${columnVisibilityKey}_order`);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.error('Failed to parse column order', e);
      }
    }
    // Default order is the order of columns prop
    return columns.map(c => c.key);
  });

  // Helper to get default visible columns (all true or use defaultVisible prop)
  const getDefaultVisible = () => {
    if (Object.keys(defaultVisible).length > 0) {
      return { ...defaultVisible };
    }
    return columns.reduce(
      (acc, col) => {
        acc[col.key] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  };

  // Load from localStorage if key is provided
  const getInitialVisibleColumns = () => {
    if (columnVisibilityKey) {
      try {
        const stored = localStorage.getItem(columnVisibilityKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          // If all columns are hidden, reset to default visible
          if (Object.values(parsed).filter(Boolean).length === 0) {
            return getDefaultVisible();
          }
          return parsed;
        }
      } catch (e) {}
    }
    // Fallback: use defaultVisible prop or all visible
    return getDefaultVisible();
  };

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    getInitialVisibleColumns(),
  );
  const [sortedData, setSortedData] = useState<any[]>(data);

  // Reconcile visibleColumns when columns change (API-driven columns)
  useEffect(() => {
    // Determine base config: localStorage (if present) else defaultVisible
    let stored: Record<string, boolean> | null = null;
    if (columnVisibilityKey) {
      try {
        const raw = localStorage.getItem(columnVisibilityKey);
        if (raw) stored = JSON.parse(raw);
      } catch (e) {}
    }

    // Build merged visibility strictly from current columns
    const merged: Record<string, boolean> = {};
    let hasAnyVisible = false;
    let needsUpdate = false;

    columns.forEach((col) => {
      // Prefer explicit value from state if it already exists for this key
      const currentVal = visibleColumns[col.key];
      if (typeof currentVal !== "undefined") {
        merged[col.key] = currentVal;
        if (currentVal) hasAnyVisible = true;
        return;
      }
      // If state didn't have it, try stored, else defaultVisible, else true
      const fromStored = stored ? stored[col.key] : undefined;
      const fromDefault =
        defaultVisible &&
        Object.prototype.hasOwnProperty.call(defaultVisible, col.key)
          ? defaultVisible[col.key]
          : undefined;
      const value =
        typeof fromStored !== "undefined"
          ? fromStored
          : typeof fromDefault !== "undefined"
            ? fromDefault
            : true;
      merged[col.key] = value;
      if (value) hasAnyVisible = true;
      needsUpdate = true; // we filled a missing key
    });

    // If the number of keys differs (columns removed), or we added missing keys, update
    if (needsUpdate || Object.keys(visibleColumns).length !== columns.length) {
      // Safety: if everything ended hidden, fall back to defaultVisible
      if (!hasAnyVisible) {
        const fallback = getDefaultVisible();
        columns.forEach((c) => {
          merged[c.key] = fallback[c.key] ?? true;
        });
      }
      setVisibleColumns(merged);
      return; // avoid extra work
    }

    // Also handle case where visibleColumns has keys no longer present; prune them
    const extraKeys = Object.keys(visibleColumns).filter(
      (k) => !columns.some((c) => c.key === k),
    );
    if (extraKeys.length > 0) {
      const pruned: Record<string, boolean> = {};
      columns.forEach((c) => {
        pruned[c.key] = visibleColumns[c.key];
      });
      if (Object.values(pruned).filter(Boolean).length === 0) {
        const fallback = getDefaultVisible();
        columns.forEach((c) => {
          pruned[c.key] = fallback[c.key] ?? true;
        });
      }
      setVisibleColumns(pruned);
    }
  }, [columns, columnVisibilityKey]);

  // Derived sorted columns based on columnOrder
  const effectiveColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return columns;
    
    // Clone to sort
    const sorted = [...columns];
    sorted.sort((a, b) => {
       const idxA = columnOrder.indexOf(a.key);
       const idxB = columnOrder.indexOf(b.key);
       // If key found, use index. If not found (e.g. new column), put at end
       const valA = idxA !== -1 ? idxA : 999;
       const valB = idxB !== -1 ? idxB : 999;
       return valA - valB;
    });
    return sorted;
  }, [columns, columnOrder]);

  const columnWidths = useMemo(() => {
    // START: Use effectiveColumns instead of columns
    const visibleKeys = effectiveColumns
      .filter((c) => visibleColumns[c.key])
      .map((c) => c.key);
    // END
    if (visibleKeys.length === 0) return {} as Record<string, string>;
    const defaultWeights = columnWeights || {}; // Use passed weights or empty object to default to 10
    const totalWeight = visibleKeys.reduce(
      (sum, key) => sum + (defaultWeights[key] || 10),
      0,
    );
    const widths: Record<string, string> = {};
    visibleKeys.forEach((key) => {
      const percentage = ((defaultWeights[key] || 10) / totalWeight) * 100;
      widths[key] = `${percentage}%`;
    });
    return widths;
  }, [effectiveColumns, visibleColumns, columnWeights]); // Updated dependency

  // Generate search options from VISIBLE columns only + extra columns
  const searchOptions = useMemo(() => {
    // START: Use effectiveColumns
    const visible = effectiveColumns.filter(
      (col) => visibleColumns[col.key] && col.key !== "action",
    );
    // END
    return [
      { value: "all", label: "All Fields" },
      ...visible.map((col) => ({ value: col.key, label: col.label })),
      ...extraFilterColumns.map((col) => ({ value: col.key, label: col.label })),
    ];
  }, [effectiveColumns, visibleColumns, extraFilterColumns]); // Updated dependency

  // If current selected field becomes hidden, reset to 'all'
  useEffect(() => {
    const isExtra = extraFilterColumns.some(c => c.key === searchField);
    if (
      searchField !== "all" &&
      !isExtra &&
      (!visibleColumns[searchField] || searchField === "action")
    ) {
      setSearchField("all");
    }
  }, [searchField, visibleColumns, extraFilterColumns]);

  // Helpers for robust, per-column search
  const normalizeForSearch = (val: any): string => {
    if (val == null) return "";
    if (Array.isArray(val))
      return val.map(normalizeForSearch).filter(Boolean).join(", ");
    if (typeof val === "object")
      return Object.values(val).map(normalizeForSearch).filter(Boolean).join(" ");
    return String(val);
  };

  const getCellSearchText = (col: Column, row: any): string => {
    const raw = col.searchValue ? col.searchValue(row) : (row as any)[col.key];
    return normalizeForSearch(raw).toLowerCase();
  };

  // Filter data based on search
  const getFilteredData = () => {
    if (!searchTerm && searchTerms.length === 0) return sortedData;
    
    // Combine existing chips with current typing (if valid)
    const activeTerms = [...searchTerms];
    const currentTyping = searchTerm.trim().toLowerCase();
    if (currentTyping) {
        activeTerms.push(currentTyping);
    }

    if (activeTerms.length === 0) return sortedData;

    return sortedData.filter((row: any) => {
      let colsToSearch: Column[] = [];

      if (searchField === "all") {
        colsToSearch = [
            ...effectiveColumns.filter((c) => visibleColumns[c.key]), 
            ...extraFilterColumns
        ];
      } else {
        const foundInEffective = effectiveColumns.find(c => c.key === searchField);
        const foundInExtra = extraFilterColumns.find(c => c.key === searchField);
        if (foundInEffective && visibleColumns[searchField]) colsToSearch.push(foundInEffective);
        if (foundInExtra) colsToSearch.push(foundInExtra);
      }
      
      if (colsToSearch.length === 0) return false;
      
      // Check if ANY term matches ANY column (OR logic across terms)
      return activeTerms.some(term => 
        colsToSearch.some((col) =>
          getCellSearchText(col, row).includes(term.toLowerCase())
        )
      );
    });
  };
  const filteredData = getFilteredData();

  // Excel download logic (visible columns, filtered data)
  const handleDownloadFile = () => {
    const visibleCols = effectiveColumns.filter((col) => visibleColumns[col.key]); // Use effectiveColumns
    const excelData = filteredData.map((row) => {
      const obj: Record<string, any> = {};
      visibleCols.forEach((col) => {
        let value;
        if (col.key === "type" || col.key === "integration_type") {
          value = row.integration_data?.integration_item_type;
        } else {
          value = row[col.key];
        }
        obj[col.label] = value;
      });
      return obj;
    });
    const listArr = [
      {
        sheetName: `CadenceTable_${+new Date()}`.substring(30),
        details: [...excelData],
      },
    ];
    exportToExcel(listArr, `CadenceTable_${+new Date()}`, true);
  };

  // Sorting logic
  const handleSortClick = (key: string) => {
    if (key === "action") return;
    const col = effectiveColumns.find((c) => c.key === key); // Use effectiveColumns
    const isSortable = col?.sortable !== false; // Default to true unless explicitly false

    // If strictly not sortable, return
    if (!isSortable) return;

    let asc = true;
    if (sortedColumn && sortedColumn.key === key) {
      asc = !sortedColumn.asc;
    }
    const sorted = [...sortedData].sort((a, b) => {
      let v1, v2;
      if (col && col.sortValue) {
        v1 = col.sortValue(a);
        v2 = col.sortValue(b);
      } else {
        if (key === "type" || key === "integration_type") {
          v1 = a.integration_data?.integration_item_type || "";
          v2 = b.integration_data?.integration_item_type || "";
        } else {
          v1 = a[key];
          v2 = b[key];
        }
      }
      if (typeof v1 === "number" && typeof v2 === "number") {
        return asc ? v1 - v2 : v2 - v1;
      }
      return asc
        ? String(v1).localeCompare(String(v2))
        : String(v2).localeCompare(String(v1));
    });
    setSortedColumn({ key, asc });
    setSortedData(sorted);
  };

  // Column visibility logic
  const handleColumnToggle = (key: string) => {
    const visibleCount = Object.values(visibleColumns).filter(Boolean).length;
    if (visibleCount === 1 && visibleColumns[key]) {
      return;
    }
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  
  // --- INTERNAL HANDLERS ---

  // Order Change Callbacks
  const handleInternalColumnOrderChange = (newOrder: string[]) => {
    setColumnOrder(newOrder);
    if (columnVisibilityKey) {
      localStorage.setItem(`${columnVisibilityKey}_order`, JSON.stringify(newOrder));
    }
  };

  // Visibility Change Callback for DataList
  const handleInternalColumnVisibilityChange = (key: string) => {
    // Just reuse local toggle logic, which already persists to localStorage in useEffect
    handleColumnToggle(key);
  };

  // Reset Callback
  const handleInternalResetColumns = () => {
    // Reset Order
    const defaultOrder = columns.map(c => c.key);
    setColumnOrder(defaultOrder);
    if (columnVisibilityKey) {
      // Explicitly overwrite with default order to ensure persistence
      localStorage.setItem(`${columnVisibilityKey}_order`, JSON.stringify(defaultOrder));
      // Also clear persisted visibility so stale data doesn't override reset
      localStorage.removeItem(columnVisibilityKey);
    }

    // Reset Visibility — build a complete map covering ALL current column keys
    const defaultVis: Record<string, boolean> = {};
    columns.forEach((col) => {
      if (Object.prototype.hasOwnProperty.call(defaultVisible, col.key)) {
        defaultVis[col.key] = defaultVisible[col.key];
      } else {
        defaultVis[col.key] = true;
      }
    });
    setVisibleColumns(defaultVis);
  };

  // Sync sortedData with data prop
  useEffect(() => {
    setSortedData(data);
  }, [JSON.stringify(data)]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target as Node)
      ) {
        setShowColumnDropdown(false);
      }
    }
    if (showColumnDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnDropdown]);

  // Persist visibleColumns to localStorage
  useEffect(() => {
    if (columnVisibilityKey) {
      localStorage.setItem(columnVisibilityKey, JSON.stringify(visibleColumns));
    }
  }, [visibleColumns, columnVisibilityKey]);

  return (
    <div className="flex flex-col h-full dark:bg-[#111928] w-full">
      <div className="flex-shrink-0 mt-3 z-30 bg-gray-50 dark:bg-[#111928] dark:border-gray-700">
        <div className="flex justify-between items-center gap-4">
          {/* Left: Search controls */}
          <div className="flex items-center ml-0 gap-2">
            <SingleSelect
              name="searchField"
              options={searchOptions}
              defaultValue={searchOptions.find(
                (opt) => opt.value === searchField,
              )}
              onChange={(option) => setSearchField(option.value)}
              placeholder={`Select Field`}
            />
            <div className="flex w-fit gap-2 relative">
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={`Search by ${searchOptions
                  .find((opt) => opt.value === searchField)
                  ?.label.toLowerCase()}...`}
              />
              {searchTerms.length > 0 && (
                <div className="flex flex-wrap gap-2 z-10 ">
                  {searchTerms.map((term, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                    >
                      {term}
                      <button
                        type="button"
                        onClick={() => handleRemoveTerm(term)}
                        className="ml-1 inline-flex items-center justify-center p-0.5 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none dark:hover:bg-blue-800 dark:text-blue-300"
                      >
                        <span className="sr-only">Remove large option</span>
                        <svg
                          className="h-2 w-2"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 8 8"
                        >
                          <path
                            strokeLinecap="round"
                            strokeWidth="1.5"
                            d="M1 1l6 6m0-6L1 7"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Right: Download and Column Visibility */}
          <div className="flex items-center">
            <button
              onClick={handleDownloadFile}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-gray-500 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100 transition-all duration-200 h-[42px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-500 dark:focus:border-blue-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <HiDocumentText className="w-5 h-5" />
              <span>Download Excel</span>
            </button>

            {filterDropdown}
          </div>
        </div>
      </div>
      <section className="flex-1 min-h-0 bg-gray-50 dark:bg-[#111928] mt-3 mb-2">
        { (
          <div className="flex">
            <span className="dark:text-gray-400 text-xs py-2">
             {searchTerm != "" ? "Showing " + filteredData.length + " of " + data?.length || 0 : "Showing All Data (" + (data?.length || 0) + ")"}
            </span>
          </div>
        )}
        <div className="relative h-full flex flex-col dark:bg-[#111928]">
          <DataList
            data={filteredData ?? []}
            // Use effectiveColumns (sorted) instead of original columns
            columns={effectiveColumns}
            visibleColumns={visibleColumns}
            columnWidths={columnWidths}
            loading={loading}
            onRowClick={onRowClick}
            onSort={handleSortClick}
            childNotPossible={true}
            sortConfig={
              sortedColumn
                ? {
                    key: sortedColumn.key,
                    direction: sortedColumn.asc ? "asc" : "desc",
                  }
                : undefined
            }
            getRowKey={(row) =>
              row.id ||
              row.item_id ||
              row.project_id ||
              Math.random().toString()
            }
            emptyMessage={"No data found."}
            skeletonRows={10}
            onColumnOrderChange={handleInternalColumnOrderChange}
            onColumnVisibilityChange={handleInternalColumnVisibilityChange}
            onResetColumns={handleInternalResetColumns}
            hiddenColumnKeys={hiddenColumnKeys}
            fixedColumnKeys={fixedColumnKeys}
            // @ts-ignore
            externalScroll={false}
            // Pass any other necessary props
          />
        </div>
      </section>
    </div>
  );
};

export default CadenceDataTable;
